import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import type { TextFieldProps } from "@mui/material/TextField";
import {
	InputValidationType,
	validateTextInput,
	stripCpr,
} from "../../../../Helpers/InputValidationHelpers";
import "./InputComponent.scss";

export interface InputComponentProps {
	change: (result: { value: string; valid: boolean; error: string }) => void;
	validationType: InputValidationType;
	placeholder?: string;
	value?: string;
	errorMessage?: string;
	customValidator?: (text: string) => boolean;
	customTextValidationError?: string;
	touchStart?: () => void;
	width?: number;
	inputProps?: TextFieldProps["inputProps"];
	disabled?: boolean;
}

export const InputComponent = ({
	change,
	validationType,
	placeholder = "",
	value: controlledValue,
	errorMessage,
	customValidator,
	customTextValidationError,
	touchStart,
	width,
	inputProps: extraInputProps,
	disabled = false,
}: InputComponentProps) => {
	const [internalValue, setInternalValue] = useState(controlledValue ?? "");
	const [touched, setTouched] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (controlledValue !== undefined) {
			setInternalValue(controlledValue);
		}
	}, [controlledValue]);

	useEffect(() => {
		if (disabled) {
			setTouched(false);
			setError("");
		}
	}, [disabled]);

	const processValue = (val: string): string => {
		if (validationType === "phone" || validationType === "number") {
			return val.replace(/\D/g, "");
		}
		return val;
	};

	const emitValue = (val: string): string => {
		if (validationType === "cpr" || validationType === "cpr_age") {
			return stripCpr(val);
		}
		return val;
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		const val = processValue(e.target.value);
		setInternalValue(val);
		if (customValidator) {
			const valid = customValidator(val);
			const err = valid ? "" : (customTextValidationError || "Invalid input");
			change({ value: emitValue(val), valid, error: err });
		} else {
			const result = validateTextInput(val, validationType, errorMessage);
			change({ value: emitValue(val), valid: result.valid, error: result.error });
		}
	};

	const handleBlur = () => {
		if (disabled) return;
		setTouched(true);
		if (customValidator) {
			const valid = customValidator(internalValue);
			const err = valid ? "" : (customTextValidationError || "Invalid input");
			setError(err);
			change({ value: emitValue(internalValue), valid, error: err });
		} else {
			const result = validateTextInput(internalValue, validationType, errorMessage);
			setError(result.error);
			change({
				value: emitValue(internalValue),
				valid: result.valid,
				error: result.error,
			});
		}
	};

	const showError = touched && error.length > 0;

	const resolvedInputProps =
		validationType === "phone" || validationType === "number"
			? { inputMode: "numeric" as const, ...(validationType === "phone" ? { maxLength: 8 } : {}), ...extraInputProps }
			: extraInputProps;

	return (
		<div className="input-component-root" style={width ? { width } : undefined}>
			<TextField
				variant="outlined"
				label={placeholder}
				value={internalValue}
				onChange={handleChange}
				onBlur={handleBlur}
				onTouchStart={touchStart}
				error={showError}
				inputProps={resolvedInputProps}
				disabled={disabled}
				fullWidth
			/>
			{showError && (
				<div className="input-component-error">{error}</div>
			)}
		</div>
	);
};

export default InputComponent;
